// Imports for external libraries go here.
import React, { FC } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { QuoteModel, QuoteProps } from './Quote.types';
import { StyledQuote } from './Quote.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Eyebrow, Image, RuleLine } from '@marriott/mi-ui-library';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';
import { useDCADynamicModelAlloy } from '../../modules/helpers';

// Use named rather than default exports.
export const Quote: FC<QuoteProps> = (props: QuoteProps) => {
  console.log('props', props);
  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const componentValues: QuoteModel = targetData ? targetData : props?.model;

  const sanitizedNode = DOMPurify.sanitize(componentValues?.quote as string, { RETURN_DOM: true }) as HTMLElement;
  const fragmentString = sanitizedNode.innerHTML;
  const QuoteTag = props?.hideQuotationMarks ? 'div' : 'q';

  return (
    <StyledQuote className={componentValues.styleclass} $hideQuotationMarks={props?.hideQuotationMarks}>
      <div className="quote container">
        <div
          className={clsx(
            componentValues?.imageAlignment === 'left' ? 'flex-md-row-reverse' : 'flex-row',
            'row px-0 luxury-quote'
          )}
        >
          {componentValues && (
            <>
              <div
                className={clsx(
                  componentValues.imageAlignment === 'right' ? 'offset-md-0 col-md-6 col-xl-7' : '',
                  componentValues.imageAlignment === 'left'
                    ? 'col-md-6 offset-md-1 col-xl-7 offset-xl-1 left-ar-text'
                    : '',
                  componentValues.quoteWithImage === 'false' ? 'col-md-12 col-xl-12' : '',
                  'offset-1 col-10'
                )}
              >
                <Eyebrow
                  text={componentValues.authorName}
                  customClass={clsx('t-overline-normal', 'quote-eyebrow', 'd-inline-block', 'mb-5')}
                />
                <RuleLine styleclass={clsx('ruler-length-100', 't-accent-rule')} />
                <div className={'mt-5 t-title-m'}>
                  <QuoteTag
                    className={clsx({ 'reverse-quotes': !props?.hideQuotationMarks })}
                    dangerouslySetInnerHTML={{ __html: fragmentString ?? '' }}
                  />
                </div>
              </div>
              {componentValues && componentValues.quoteWithImage === 'true' && (
                <div
                  className={clsx(
                    componentValues.imageAlignment === 'right' ? 'offset-md-1 col-md-5 col-xl-4 right-ar-image' : '',
                    componentValues.imageAlignment === 'left' ? 'offset-md-0 col-md-5 col-xl-4' : '',
                    'mt-4 mt-md-0 offset-2 col-8'
                  )}
                >
                  <Image
                    customClass={'luxury-quote-image'}
                    dynamic={true}
                    loading={'lazy'}
                    defaultImageURL={componentValues.dynamicImage?.assetPath}
                    renditions={componentValues.dynamicImage?.renditions}
                    altText={componentValues.dynamicImage?.altText}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </StyledQuote>
  );
};

export const QuoteConfig = {
  emptyLabel: 'LuxuryQuote',
  isEmpty: () => true,
  resourceType: `mi-aem-luxury-spa/components/content/quote/v1/quote`,
};

export const QuoteEditable = (props: QuoteProps) => (
  <div data-testid="luxury-Quote" data-component-name="m-luxury-Quote">
    <EditableComponent config={QuoteConfig} {...props}>
      <Quote {...props} />
    </EditableComponent>
  </div>
);
